import React, { useRef, useState, useEffect, useMemo } from "react";
import ReactDOMServer from "react-dom/server";
import { Search } from "lucide-react";
import { BsFiletypePdf } from "react-icons/bs";
import LicenseCard from "../../Screens/Common/LicenseCard";
import PortalModal from "../PortalModal";
import { useAssets } from "../../Context/AssetsContext";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import img from "../../Assets/Logos/profile3.jpg";
import logo from "../../Assets/Logos/ashoklogo.png";
import axios from "axios";
import { toast } from "react-toastify";

export default function DrivingLicense() {
  const { assetsReady } = useAssets();
  const [dlNumber, setDlNumber] = useState("");
  const [dob, setDob] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [dlData, setDLData] = useState(null);
  const [showCard, setShowCard] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState(null);
  const [history, setHistory] = useState([]);
  const [filteredHistory, setFilteredHistory] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const printRef = useRef();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  // ✅ Signature selection state
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [includeSignature, setIncludeSignature] = useState(false);

  // ✅ Hardcoded Cloudinary signature URL - REPLACE WITH YOUR ACTUAL URL
  const SIGNATURE_URL =
    "https://res.cloudinary.com/dfjeuiwro/image/upload/v1769506708/DL-NEW_h6tx9m.png";

  const [filters, setFilters] = useState({
    search: "",
    status: "",
    fromDate: "",
    toDate: "",
  });

  // Get current user and their servicePrices
  const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
  const defaultPrices = {
    aadhaar: 10,
    pan: 10,
    smartrc: 25,
    voterId: 10,
    drivingLicense: 10,
    parivaanrc: 10,
    digilockerrc: 10,
    DL_Signature: 1,
  };

  const drivingPrice = Number(
    currentUser?.servicePrices?.drivingLicense ?? defaultPrices.drivingLicense,
  );
  const signaturePrice = Number(
    currentUser?.servicePrices?.DL_Signature ?? defaultPrices.DL_Signature,
  );

  // ✅ Check if user has DL_Signature access
  const hasSignatureAccess =
    currentUser?.serviceAccess?.includes("DL_Signature") || false;

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [filters, history]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/services/get-document-records?service=driving-license`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );

      const historyData = response.data.map((item, index) => ({
        date: new Date(item.createdAt).toLocaleString(),
        slNo: `${index + 1}`.padStart(3, "0"),
        dlNumber: item.idNumber,
        name: item.apiResponse?.name,
        status: item.status === "pending" ? "Failed" : "Success",
        error: item.status === "failed" ? "Verification failed" : "",
        createdAt: new Date(item.createdAt),
      }));

      setHistory(historyData);
      setFilteredHistory(historyData);
    } catch (err) {
      console.error("Error fetching data:", err);
      toast.error("Failed to fetch data. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async () => {
    setError("");
    if (!dlNumber || !dob) {
      return setError("Please enter both DL Number and Date of Birth");
    }

    // ✅ Show signature modal if user has access, otherwise show confirmation modal
    if (hasSignatureAccess) {
      setShowSignatureModal(true);
    } else {
      setShowConfirmationModal(true);
    }
  };

  // ✅ Direct payment from signature modal
  const handleConfirmAndPay = () => {
    setShowSignatureModal(false);
    proceedWithApiCall();
  };

  const proceedWithApiCall = async () => {
    try {
      setIsLoading(true);
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/services/dl/driving-license-verification`,
        {
          id_number: dlNumber,
          dob: dob,
          include_signature: includeSignature, // ✅ Send signature flag
        },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        },
      );

      if (response.data.success === false) {
        throw new Error(response.data.message || "Failed to fetch DL data");
      }

      const licenseData = response.data.data;
      if (
        licenseData.licenseDetails.hasProfileImage &&
        !licenseData.licenseDetails.profileImage.startsWith("data:")
      ) {
        licenseData.licenseDetails.profileImage = `data:image/jpeg;base64,${licenseData.licenseDetails.profileImage}`;
      }

      // ✅ Use hardcoded signature URL if signature is included
      if (includeSignature) {
        licenseData.signatureUrl = SIGNATURE_URL;
      }

      setDLData(licenseData);
      setShowCard(true);
      setSelectedLicense(licenseData);

      const newEntry = {
        date: new Date().toLocaleString(),
        slNo: `${history.length + 1}`.padStart(3, "0"),
        dlNumber: response.data.data.licenseDetails.licenseNumber,
        name: response.data.data.licenseDetails.personalInfo.name,
        dob: formatDate(response.data.data.licenseDetails.personalInfo.dob),
        status: "Success",
      };
      updateHistory(newEntry);

      // ✅ Show price breakdown in toast
      const breakdown = response.data.priceBreakdown;
      if (breakdown) {
        let message = `DL verified! Base: ₹${breakdown.basePrice}`;
        if (includeSignature && breakdown.signatureFee > 0) {
          message += ` + Signature: ₹${breakdown.signatureFee}`;
        }
        message += ` = Total: ₹${breakdown.total}`;
        toast.success(message);
      } else {
        toast.success("DL data fetched successfully");
      }

      // ✅ Reset signature selection after successful API call
      // Note: We don't reset here so the signature persists in the preview
    } catch (err) {
      console.error("Error fetching DL data:", err);

      let errorMessage = "Failed to fetch DL data. Please try again.";
      if (err.response) {
        if (err.response.data && err.response.data.message) {
          errorMessage = err.response.data.message;
        } else if (err.response.status === 401) {
          errorMessage = "Unauthorized - Please login again";
        } else if (err.response.status === 403) {
          errorMessage = "Forbidden - You don't have permission";
        } else if (err.response.status === 404) {
          errorMessage = "Service not found";
        } else if (err.response.status === 500) {
          errorMessage = "Server error - Please try again later";
        }
      } else if (err.request) {
        errorMessage = "Network error - Please check your connection";
      }

      toast.error(errorMessage);

      const newEntry = {
        date: new Date().toLocaleString(),
        slNo: `${history.length + 1}`.padStart(3, "0"),
        dlNumber: dlNumber,
        name: " ",
        dob: " ",
        status: "Failed",
        error: errorMessage,
      };
      updateHistory(newEntry);
    } finally {
      setIsLoading(false);
    }
  };

  const applyFilters = () => {
    let result = [...history];

    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      result = result.filter(
        (item) =>
          item.dlNumber.toLowerCase().includes(searchTerm) ||
          (item.name && item.name.toLowerCase().includes(searchTerm)),
      );
    }

    if (filters.status) {
      result = result.filter((item) => item.status === filters.status);
    }

    if (filters.fromDate) {
      const fromDate = new Date(filters.fromDate);
      result = result.filter((item) => new Date(item.createdAt) >= fromDate);
    }

    if (filters.toDate) {
      const toDate = new Date(filters.toDate);
      toDate.setHours(23, 59, 59, 999);
      result = result.filter((item) => new Date(item.createdAt) <= toDate);
    }

    setFilteredHistory(result);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({
      search: "",
      status: "",
      fromDate: "",
      toDate: "",
    });
    setFilteredHistory([...history]);
  };

  const totalPages = Math.ceil(filteredHistory.length / itemsPerPage);
  const currentData = filteredHistory.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const handleDownloadPDF = async () => {
    if (!assetsReady) {
      toast.warning("Please wait, assets are still preloading...");
      return;
    }
    if (!printRef.current) return;
    try {
      const cardElement = printRef.current;
      const canvas = await html2canvas(cardElement, {
        scale: 3,
        useCORS: true, // ✅ Enable CORS for Cloudinary images
        allowTaint: true, // ✅ Allow cross-origin images
        backgroundColor: "#ffffff",
        logging: false, // Disable logging for cleaner output
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("portrait", "pt", "a4");

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio =
        Math.min(pageWidth / imgWidth, pageHeight / imgHeight) * 0.95;
      const scaledWidth = imgWidth * ratio;
      const scaledHeight = imgHeight * ratio;

      const marginX = (pageWidth - scaledWidth) / 2;
      const marginY = 30;

      pdf.addImage(imgData, "PNG", marginX, marginY, scaledWidth, scaledHeight);
      pdf.save(`DL-${selectedLicense.licenseDetails.licenseNumber}.pdf`);
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Failed to generate PDF");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === "1800-01-01") return "";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const updateHistory = (entry) => {
    setHistory((prev) => [entry, ...prev]);
  };

  const handlePrev = () => setCurrentPage((prev) => Math.max(prev - 1, 1));
  const handleNext = () =>
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));

  const visiblePages = useMemo(() => {
    const delta = 2;
    const pages = [];
    for (
      let i = Math.max(1, currentPage - delta);
      i <= Math.min(totalPages, currentPage + delta);
      i++
    ) {
      pages.push(i);
    }
    return pages;
  }, [currentPage, totalPages]);

  return (
    <div className="w-full mx-auto md:py-4">
      <div className="bg-blue-100 p-2">
        <h2 className="text-center text-xl font-semibold text-blue-700">
          Driving License
        </h2>
      </div>

      <div className="py-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 flex flex-col">
            <label className="mb-1 text-sm text-gray-600">
              Enter DL Number
            </label>
            <input
              type="text"
              placeholder="e.g. OD0420110012345"
              value={dlNumber}
              onChange={(e) => setDlNumber(e.target.value.toUpperCase())}
              className="w-full px-4 py-2 border rounded"
            />
          </div>

          <div className="flex-1 flex flex-col">
            <label className="mb-1 text-sm text-gray-600">
              Enter Date of Birth
            </label>
            <input
              type="date"
              value={dob}
              onChange={(e) => setDob(e.target.value)}
              className="w-full px-4 py-2 border rounded"
            />
          </div>
        </div>

        <div className="flex items-center gap-2 mt-4">
          <button
            onClick={handleSubmit}
            disabled={isLoading || !dlNumber || !dob}
            className={`bg-indigo-600 text-white py-2 px-6 rounded hover:bg-indigo-700 ${
              isLoading ? "opacity-70 cursor-not-allowed" : ""
            }`}
          >
            {isLoading ? "Processing..." : "SUBMIT"}
          </button>
          <span className="text-red-600 block">₹{drivingPrice}</span>
        </div>
        {error && <p className="text-red-500 mt-2">{error}</p>}
      </div>

      {/* History and filters section */}
      <div className="w-full">
        <div className="bg-white p-4 shadow-md rounded-lg mb-4">
          <h3 className="text-lg font-semibold mb-4">
            Driving License Results
          </h3>

          <div className="bg-gray-50 p-4 rounded-lg mb-4">
            <h4 className="text-md font-medium mb-3">Filter Current Results</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium mb-1">Search</label>
                <input
                  type="text"
                  placeholder="Search DL number"
                  value={filters.search}
                  onChange={(e) =>
                    setFilters({ ...filters, search: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Status</label>
                <select
                  value={filters.status}
                  onChange={(e) =>
                    setFilters({ ...filters, status: e.target.value })
                  }
                  className="w-full border rounded px-3 py-2"
                >
                  <option value="">All Status</option>
                  <option value="Success">Success</option>
                  <option value="Failed">Failed</option>
                  <option value="pending">Pending</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  From Date
                </label>
                <input
                  type="date"
                  value={filters.fromDate}
                  onChange={(e) =>
                    setFilters({ ...filters, fromDate: e.target.value })
                  }
                  className="w-full border rounded px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  To Date
                </label>
                <input
                  type="date"
                  value={filters.toDate}
                  onChange={(e) =>
                    setFilters({ ...filters, toDate: e.target.value })
                  }
                  className="w-full border rounded px-3 py-2"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={clearFilters}
                className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
              >
                Clear
              </button>
              <button
                onClick={applyFilters}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Apply
              </button>
            </div>
          </div>

          <div className="bg-white shadow rounded-lg overflow-x-auto">
            <table className="min-w-full text-sm border-b-2 text-left">
              <thead className="bg-gray-100 text-gray-700 border-2">
                <tr>
                  <th className="px-4 py-2">Sl No</th>
                  <th className="px-4 py-2">Date & Time</th>
                  <th className="px-4 py-2">DL ID</th>
                  <th className="px-4 py-2">Name</th>
                  <th className="px-4 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {currentData.map((item, idx) => (
                  <tr key={idx} className="border-t">
                    <td className="px-4 py-2">{item.slNo}</td>
                    <td className="px-4 py-2">{item.date}</td>
                    <td className="px-4 py-2">{item.dlNumber}</td>
                    <td className="px-4 py-2">{item.name}</td>
                    <td className="px-4 py-2">
                      <span
                        className={`px-2 py-1 rounded text-white text-xs ${
                          item.status === "Success"
                            ? "bg-green-500"
                            : "bg-red-500"
                        }`}
                      >
                        {item.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col md:flex-row items-center justify-between mt-4">
            <span className="text-sm text-gray-500">
              Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
              {Math.min(currentPage * itemsPerPage, filteredHistory.length)} of{" "}
              {filteredHistory.length} entries
            </span>

            <div className="flex gap-2 mt-2 md:mt-0">
              <button
                onClick={handlePrev}
                disabled={currentPage === 1}
                className="px-3 py-1 border rounded text-sm disabled:opacity-50"
              >
                Prev
              </button>

              {visiblePages[0] > 1 && <span className="px-2">...</span>}
              {visiblePages.map((p) => (
                <button
                  key={p}
                  onClick={() => setCurrentPage(p)}
                  className={`px-3 py-1 border rounded text-sm ${
                    currentPage === p ? "bg-blue-600 text-white" : ""
                  }`}
                >
                  {p}
                </button>
              ))}
              {visiblePages[visiblePages.length - 1] < totalPages && (
                <span className="px-2">...</span>
              )}

              <button
                onClick={handleNext}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border rounded text-sm disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>

          {/* ✅ SIGNATURE MODAL - Shows only if user has DL_Signature access */}
          {showSignatureModal && hasSignatureAccess && (
            <PortalModal>
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                <div className="bg-white rounded-lg shadow-lg w-full max-w-lg p-6">
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold">
                      DL Verification Options
                    </h3>
                  </div>

                  {/* Signature Selection */}
                  <div className="bg-blue-50 p-4 rounded border border-blue-200 mb-6">
                    <h4 className="font-semibold text-sm mb-4">
                      DL Card Customization
                    </h4>

                    <div className="space-y-4">
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          id="includeSignature"
                          checked={includeSignature}
                          onChange={(e) =>
                            setIncludeSignature(e.target.checked)
                          }
                          className="w-4 h-4 cursor-pointer"
                        />
                        <label
                          htmlFor="includeSignature"
                          className="cursor-pointer flex-1"
                        >
                          <span className="font-medium text-sm">
                            Include Authorized Signature
                          </span>
                          <p className="text-xs text-gray-500">
                            Adds digital signature on the DL card
                          </p>
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Payment Summary */}
                  <div className="bg-gray-50 p-4 rounded border border-gray-200 mb-6">
                    <h4 className="font-semibold text-sm mb-4">
                      Payment Summary
                    </h4>

                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span>DL Verification:</span>
                        <span className="font-semibold">₹{drivingPrice}</span>
                      </div>

                      {includeSignature && (
                        <>
                          <div className="flex justify-between text-green-600">
                            <span>+ Signature Service:</span>
                            <span className="font-semibold">
                              ₹{signaturePrice}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500">
                            * Adds authorized digital signature
                          </div>
                        </>
                      )}

                      <hr className="my-2" />

                      <div className="flex justify-between text-lg font-bold">
                        <span>Total Amount:</span>
                        <span className="text-red-600">
                          ₹
                          {drivingPrice +
                            (includeSignature ? signaturePrice : 0)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-4">
                    <button
                      onClick={() => {
                        setIncludeSignature(false);
                        setShowSignatureModal(false);
                      }}
                      className="flex-1 px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleConfirmAndPay}
                      disabled={isLoading}
                      className={`flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 ${
                        isLoading ? "opacity-70 cursor-not-allowed" : ""
                      }`}
                    >
                      {isLoading ? "Processing..." : "Confirm & Pay"}
                    </button>
                  </div>
                </div>
              </div>
            </PortalModal>
          )}

          {/* ✅ CONFIRMATION MODAL - For users without signature access */}
          {showConfirmationModal && (
            <PortalModal>
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
                  <h3 className="text-lg font-semibold mb-4">
                    Confirm Payment
                  </h3>

                  <div className="mb-4 space-y-2">
                    <div className="flex justify-between">
                      <span>DL Verification:</span>
                      <span className="font-semibold">₹{drivingPrice}</span>
                    </div>

                    <hr className="my-2" />

                    <div className="flex justify-between text-lg font-bold">
                      <span>Total Amount:</span>
                      <span className="text-red-600">₹{drivingPrice}</span>
                    </div>
                  </div>

                  <p className="mb-4 text-sm text-gray-600">
                    Do you want to proceed with the payment?
                  </p>

                  <div className="flex justify-end gap-4">
                    <button
                      onClick={() => {
                        setShowConfirmationModal(false);
                        setIncludeSignature(false);
                      }}
                      className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => {
                        setShowConfirmationModal(false);
                        proceedWithApiCall();
                      }}
                      disabled={isLoading}
                      className={`px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 ${
                        isLoading ? "opacity-70 cursor-not-allowed" : ""
                      }`}
                    >
                      {isLoading ? "Processing..." : "Confirm & Pay"}
                    </button>
                  </div>
                </div>
              </div>
            </PortalModal>
          )}

          {/* License Preview Modal */}
          {selectedLicense && (
            <PortalModal>
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-auto">
                <div className="bg-white p-4 rounded-lg max-w-full max-h-screen overflow-auto">
                  <h3 className="text-lg font-semibold mb-4">
                    License Preview
                  </h3>
                  <div className="flex justify-center">
                    <div ref={printRef}>
                      <LicenseCard
                        data={{
                          dlId: selectedLicense.licenseDetails.licenseNumber,
                          name: selectedLicense.licenseDetails.personalInfo
                            .name,
                          fatherName:
                            selectedLicense.licenseDetails.personalInfo
                              .fatherOrHusbandName,
                          dob: formatDate(
                            selectedLicense.licenseDetails.personalInfo.dob,
                          ),
                          bloodGroup:
                            selectedLicense.licenseDetails.personalInfo
                              .bloodGroup || "Not specified",
                          address:
                            selectedLicense.licenseDetails.address.permanent,
                          state: selectedLicense.licenseDetails.address.state,
                          issueDate: formatDate(
                            selectedLicense.licenseDetails.licenseInfo
                              .issueDate,
                          ),
                          validNT: formatDate(
                            selectedLicense.licenseDetails.licenseInfo
                              .expiryDate,
                          ),
                          validTR: formatDate(
                            selectedLicense.licenseDetails.licenseInfo
                              .transportExpiryDate,
                          ),
                          rto: selectedLicense.licenseDetails.licenseInfo.ola
                            .name,
                          profileImage:
                            selectedLicense.licenseDetails.profileImage,
                          signatureImage: selectedLicense.signatureUrl || null, // ✅ Pass signature URL from state
                          vehicles:
                            selectedLicense.licenseDetails.licenseInfo.vehicleClasses.map(
                              (v) => ({
                                icon: v.includes("MCWG")
                                  ? "bike"
                                  : v.includes("LMV")
                                    ? "car"
                                    : "truck",
                                code: v,
                                issued: formatDate(
                                  selectedLicense.licenseDetails.licenseInfo
                                    .issueDate,
                                ),
                                date: formatDate(
                                  selectedLicense.licenseDetails.licenseInfo
                                    .issueDate,
                                ),
                                cat: "  ",
                                badge: "  ",
                                badgeDate: "  ",
                                badgeBy: "  ",
                              }),
                            ),
                        }}
                      />
                    </div>
                  </div>
                  <div className="flex justify-between gap-2 mt-4">
                    <button
                      onClick={() => setSelectedLicense(null)}
                      className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
                    >
                      Close
                    </button>
                     <button
                      onClick={handleDownloadPDF}
                      disabled={!assetsReady}
                      className={`px-4 py-2 text-white rounded ${
                        !assetsReady ? "bg-blue-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"
                      }`}
                    >
                      {!assetsReady ? "Loading..." : "Download PDF"}
                    </button>
                  </div>
                </div>
              </div>
            </PortalModal>
          )}
        </div>
      </div>
    </div>
  );
}
